import React, { useMemo, useState } from 'react';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { DeskProjectCard } from './DeskProjectCard';
import { ProjectsEmptyState, TrashEmptyState } from './ProjectsEmptyState';
import type { DeskProject } from '../types/desk-project';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { RotateCcw } from 'lucide-react';
import { CardMetaText } from './CardMetaText';
import { notePreview } from '../utils/note-utils';
import { useLocale } from '../contexts/LocaleContext';
import {
  resolveTrashTypeFilter,
  TrashTypeFilter,
  TRASH_TYPE_FILTER_ALL,
} from './TrashTypeFilter';

export interface DeskTrashedNote {
  id: string;
  content: string;
  updatedAt: string;
}

interface DeskProjectListProps {
  projects: DeskProject[];
  isTrash: boolean;
  error: string | null;
  onDelete: (projectId: string) => void;
  onRestore: (projectId: string) => void;
  trashedNotes?: DeskTrashedNote[];
  onRestoreNote?: (noteId: string) => void;
  restoringNoteId?: string | null;
  emptyStateActions?: React.ReactNode;
}

const NOTE_PREVIEW_MAX_LEN = 200;

export const DeskProjectList: React.FC<DeskProjectListProps> = ({
  projects,
  isTrash,
  error,
  onDelete,
  onRestore,
  trashedNotes = [],
  onRestoreNote,
  restoringNoteId = null,
  emptyStateActions,
}) => {
  const { t } = useLocale();
  const [typeFilter, setTypeFilter] = useState(TRASH_TYPE_FILTER_ALL);
  const isEmpty = projects.length === 0 && (!isTrash || trashedNotes.length === 0);

  const trashTypeOptions = useMemo(
    () => [
      { id: 'projects', label: t('Projects'), count: projects.length },
      { id: 'notes', label: t('Notes'), count: trashedNotes.length },
    ],
    [projects.length, trashedNotes.length, t]
  );

  const activeTypeFilter = resolveTrashTypeFilter(typeFilter, trashTypeOptions);
  const showProjects = !isTrash || activeTypeFilter === TRASH_TYPE_FILTER_ALL || activeTypeFilter === 'projects';
  const showNotes = isTrash && (activeTypeFilter === TRASH_TYPE_FILTER_ALL || activeTypeFilter === 'notes');

  return (
    <div className={PANEL_BODY_FILL_CLASSNAME}>
      {error && (
        <div style={{ color: '#ef4444', marginBottom: '2rem', textAlign: 'center' }}>
          {error}
        </div>
      )}
      {isEmpty ? (
        isTrash ? (
          <TrashEmptyState />
        ) : (
          <ProjectsEmptyState actions={emptyStateActions} />
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {isTrash && (
            <TrashTypeFilter
              value={activeTypeFilter}
              onChange={setTypeFilter}
              options={trashTypeOptions}
            />
          )}
          {showProjects &&
            projects.map((project) => (
              <DeskProjectCard
                key={project.id}
                project={project}
                isTrash={isTrash}
                onDelete={onDelete}
                onRestore={onRestore}
              />
            ))}
          {showNotes &&
            trashedNotes.map((note) => (
              <Card
                key={note.id}
                className="relative border-0 shadow-none rounded-[10px] transition-all bg-card"
                style={{ border: '1px solid var(--border)' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {notePreview(note.content, NOTE_PREVIEW_MAX_LEN) || t('no_content') || '(No content)'}
                    </p>
                    <CardMetaText className="mt-2">
                      {new Date(note.updatedAt).toLocaleDateString()}
                    </CardMetaText>
                  </div>
                  {onRestoreNote && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRestoreNote(note.id)}
                      disabled={restoringNoteId === note.id}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {restoringNoteId === note.id
                        ? (t('versionHistory.restoring') || 'Restoring...')
                        : t('restore')}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
};
